import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  KeyRound,
  LockKeyhole,
  Pencil,
  RefreshCcw,
  Save,
  ShieldCheck,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { api } from "../api";
import { AdminAiSettings } from "../components/AdminAiSettings";
import { Button, Card, SectionTitle, Toast } from "../components/ui";

type AdminAccount = {
  id: string;
  displayName: string;
  role: "admin" | "member";
  disabled: boolean;
  createdAt: string;
};

type AdminAccounts = { accounts: AdminAccount[] };

export function AdminPage() {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState("");
  const [createForm, setCreateForm] = useState({
    displayName: "",
    currentPassword: "",
    password: "",
    confirmPassword: "",
  });
  const [resetForm, setResetForm] = useState({
    accountId: "",
    adminPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [editingId, setEditingId] = useState("");
  const [editingName, setEditingName] = useState("");

  const accounts = useQuery({
    queryKey: ["admin-accounts"],
    queryFn: () => api<AdminAccounts>("/api/admin/accounts"),
  });

  useEffect(() => {
    if (!resetForm.accountId) {
      const first =
        accounts.data?.accounts.find((account) => account.role === "member") ||
        accounts.data?.accounts[0];
      if (first)
        setResetForm((current) => ({ ...current, accountId: first.id }));
    }
  }, [accounts.data, resetForm.accountId]);

  const refreshAccounts = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin-accounts"] }),
      queryClient.invalidateQueries({ queryKey: ["auth"] }),
    ]);
  };

  const createAccount = useMutation({
    mutationFn: () =>
      api("/api/admin/accounts", {
        method: "POST",
        body: JSON.stringify(createForm),
      }),
    onSuccess: async () => {
      setCreateForm({
        displayName: "",
        currentPassword: "",
        password: "",
        confirmPassword: "",
      });
      setToast("新账户已创建；任务、旅行和学习记录将与其他账户分开保存");
      await refreshAccounts();
    },
    onError: (error) =>
      setToast(error instanceof Error ? error.message : "创建账户失败"),
  });

  const updateAccount = useMutation({
    mutationFn: ({
      id,
      value,
    }: {
      id: string;
      value: { displayName?: string; disabled?: boolean };
    }) =>
      api(`/api/admin/accounts/${id}`, {
        method: "PATCH",
        body: JSON.stringify(value),
      }),
    onSuccess: async () => {
      setEditingId("");
      setEditingName("");
      setToast("账户信息已更新");
      await refreshAccounts();
    },
    onError: (error) =>
      setToast(error instanceof Error ? error.message : "账户更新失败"),
  });

  const resetPassword = useMutation({
    mutationFn: () =>
      api(`/api/admin/accounts/${resetForm.accountId}/password`, {
        method: "PUT",
        body: JSON.stringify({
          adminPassword: resetForm.adminPassword,
          newPassword: resetForm.newPassword,
          confirmPassword: resetForm.confirmPassword,
        }),
      }),
    onSuccess: () => {
      setResetForm((current) => ({
        ...current,
        adminPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));
      setToast("密码已重置；该账户在其他设备上的旧会话已经失效");
    },
    onError: (error) =>
      setToast(error instanceof Error ? error.message : "密码重置失败"),
  });

  if (accounts.isError) {
    return (
      <Card className="admin-denied">
        <ShieldCheck />
        <h2>无法进入管理后台</h2>
        <p>
          {accounts.error instanceof Error
            ? accounts.error.message
            : "只有管理员可以访问"}
        </p>
      </Card>
    );
  }

  return (
    <div className="admin-page" data-ai-sensitive="true">
      {toast && <Toast message={toast} />}
      <Card className="admin-hero">
        <div>
          <span>PRIVATE ADMIN CONSOLE</span>
          <h2>账户管理后台</h2>
          <p>
            创建、停用和维护登录账户，并统一配置所有账户共同使用的 API 与 AI
            线路。
          </p>
        </div>
        <ShieldCheck />
      </Card>

      <div className="admin-grid">
        <Card className="admin-accounts-card">
          <SectionTitle
            title="全部账户"
            subtitle="停用账户会立即注销它在所有设备上的会话"
            action={<Users color="var(--lav)" />}
          />
          <div className="admin-account-list">
            {accounts.data?.accounts.map((account) => (
              <article
                key={account.id}
                className={account.disabled ? "disabled" : ""}
              >
                <span className="admin-account-avatar">
                  {account.displayName.slice(0, 1)}
                </span>
                <div className="admin-account-copy">
                  {editingId === account.id ? (
                    <div className="admin-inline-edit">
                      <input
                        className="input"
                        maxLength={30}
                        value={editingName}
                        autoFocus
                        onChange={(event) => setEditingName(event.target.value)}
                      />
                      <button
                        type="button"
                        aria-label="保存昵称"
                        disabled={
                          !editingName.trim() || updateAccount.isPending
                        }
                        onClick={() =>
                          updateAccount.mutate({
                            id: account.id,
                            value: { displayName: editingName.trim() },
                          })
                        }
                      >
                        <Save />
                      </button>
                      <button
                        type="button"
                        aria-label="取消修改"
                        onClick={() => setEditingId("")}
                      >
                        <X />
                      </button>
                    </div>
                  ) : (
                    <>
                      <b>{account.displayName}</b>
                      <small>
                        {account.role === "admin" ? "管理员" : "普通账户"} ·{" "}
                        {account.disabled ? "已停用" : "使用中"}
                      </small>
                    </>
                  )}
                </div>
                <div className="admin-account-actions">
                  {editingId !== account.id && (
                    <button
                      type="button"
                      aria-label={`修改 ${account.displayName} 的昵称`}
                      onClick={() => {
                        setEditingId(account.id);
                        setEditingName(account.displayName);
                      }}
                    >
                      <Pencil />
                    </button>
                  )}
                  {account.role === "member" ? (
                    <button
                      type="button"
                      className={account.disabled ? "enable" : "disable"}
                      disabled={updateAccount.isPending}
                      onClick={() =>
                        updateAccount.mutate({
                          id: account.id,
                          value: { disabled: !account.disabled },
                        })
                      }
                    >
                      {account.disabled ? "启用" : "停用"}
                    </button>
                  ) : (
                    <em>
                      <CheckCircle2 />
                      主管理员
                    </em>
                  )}
                </div>
              </article>
            ))}
          </div>
        </Card>

        <Card>
          <SectionTitle
            title="新增普通账户"
            subtitle="新账户拥有独立的个人数据和偏好"
            action={<UserPlus color="var(--mint)" />}
          />
          <div className="admin-form">
            <div className="field">
              <label>新账户昵称</label>
              <input
                className="input"
                value={createForm.displayName}
                onChange={(event) =>
                  setCreateForm({
                    ...createForm,
                    displayName: event.target.value,
                  })
                }
                placeholder="例如：家人、学习账号"
              />
            </div>
            <div className="field">
              <label>管理员当前密码</label>
              <div className="input-icon">
                <LockKeyhole />
                <input
                  type="password"
                  value={createForm.currentPassword}
                  onChange={(event) =>
                    setCreateForm({
                      ...createForm,
                      currentPassword: event.target.value,
                    })
                  }
                  autoComplete="current-password"
                  placeholder="确认管理员身份"
                />
              </div>
            </div>
            <div className="field">
              <label>新账户密码</label>
              <div className="input-icon">
                <KeyRound />
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(event) =>
                    setCreateForm({
                      ...createForm,
                      password: event.target.value,
                    })
                  }
                  autoComplete="new-password"
                  placeholder="至少 6 位"
                />
              </div>
            </div>
            <div className="field">
              <label>确认新账户密码</label>
              <div className="input-icon">
                <KeyRound />
                <input
                  type="password"
                  value={createForm.confirmPassword}
                  onChange={(event) =>
                    setCreateForm({
                      ...createForm,
                      confirmPassword: event.target.value,
                    })
                  }
                  autoComplete="new-password"
                  placeholder="再次输入"
                />
              </div>
            </div>
            <Button
              loading={createAccount.isPending}
              disabled={
                !createForm.displayName.trim() ||
                !createForm.currentPassword ||
                createForm.password.length < 6 ||
                !createForm.confirmPassword
              }
              onClick={() => createAccount.mutate()}
            >
              <UserPlus />
              创建账户
            </Button>
          </div>
        </Card>

        <Card>
          <SectionTitle
            title="管理员重置密码"
            subtitle="无需知道成员旧密码；操作前验证管理员密码"
            action={<RefreshCcw color="var(--rose)" />}
          />
          <div className="admin-form">
            <div className="field">
              <label>目标账户</label>
              <select
                className="select"
                value={resetForm.accountId}
                onChange={(event) =>
                  setResetForm({ ...resetForm, accountId: event.target.value })
                }
              >
                {accounts.data?.accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.displayName} ·{" "}
                    {account.role === "admin" ? "管理员" : "普通账户"}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>管理员密码</label>
              <div className="input-icon">
                <LockKeyhole />
                <input
                  type="password"
                  value={resetForm.adminPassword}
                  onChange={(event) =>
                    setResetForm({
                      ...resetForm,
                      adminPassword: event.target.value,
                    })
                  }
                  autoComplete="current-password"
                  placeholder="确认管理员身份"
                />
              </div>
            </div>
            <div className="field">
              <label>目标账户新密码</label>
              <div className="input-icon">
                <KeyRound />
                <input
                  type="password"
                  value={resetForm.newPassword}
                  onChange={(event) =>
                    setResetForm({
                      ...resetForm,
                      newPassword: event.target.value,
                    })
                  }
                  autoComplete="new-password"
                  placeholder="至少 6 位"
                />
              </div>
            </div>
            <div className="field">
              <label>再次输入新密码</label>
              <div className="input-icon">
                <KeyRound />
                <input
                  type="password"
                  value={resetForm.confirmPassword}
                  onChange={(event) =>
                    setResetForm({
                      ...resetForm,
                      confirmPassword: event.target.value,
                    })
                  }
                  autoComplete="new-password"
                  placeholder="再次输入"
                />
              </div>
            </div>
            <Button
              loading={resetPassword.isPending}
              disabled={
                !resetForm.accountId ||
                !resetForm.adminPassword ||
                resetForm.newPassword.length < 6 ||
                !resetForm.confirmPassword
              }
              onClick={() => resetPassword.mutate()}
            >
              <RefreshCcw />
              重置密码
            </Button>
          </div>
        </Card>
      </div>
      <AdminAiSettings />
    </div>
  );
}
